/**
 * Serviço para integração com Google Maps API
 * Fornece funcionalidades de autocomplete de endereços
 */
import { loadGoogleMapsApi, createAutocompleteService, createPlacesService, getCurrentApiKey, resetApiLoadingState } from '@/plugins/googleMapsLoader';

// State variables
let autocompleteService = null;
let placesService = null;
let authErrorOccurred = false;

/**
 * Dispara um evento de erro de autenticação com a chave atual
 */
const dispatchAuthError = () => {
  authErrorOccurred = true;

  // Limpar serviços para forçar reinicialização
  autocompleteService = null;
  placesService = null;

  // Disparar evento para notificar componentes
  document.dispatchEvent(new CustomEvent('google-maps-auth-error', {
    detail: { apiKey: getCurrentApiKey() }
  }));
};

// Adicionar listener para erros de autenticação
document.addEventListener('google-maps-auth-error', dispatchAuthError);

/**
 * Inicializa os serviços do Google Maps
 * @returns {Promise<Object>} Objeto contendo os serviços inicializados
 */
const initServices = async () => {
  // Se já ocorreu um erro de autenticação, não tenta novamente inicializar
  if (authErrorOccurred) {
    throw new Error('Erro de autenticação na API do Google Maps');
  }

  if (!autocompleteService || !placesService) {
    try {
      await loadGoogleMapsApi();
      autocompleteService = await createAutocompleteService();
      placesService = await createPlacesService();
    } catch (error) {
      // Se for um erro de autenticação, vamos sinalizar isso
      if (error.message.includes('authentication')) {
        dispatchAuthError();
      }
      throw error;
    }
  }
  return { autocompleteService, placesService };
};

/**
 * Verifica o status da API e dispara erro se necessário
 * @param {string} status O status retornado pela API 
 */
const checkApiStatus = (status) => {
  if (status === 'REQUEST_DENIED') {
    dispatchAuthError();
    throw new Error('Erro de autenticação na API do Google Maps');
  }
};

/**
 * Busca sugestões de endereços baseado em um texto de entrada
 * @param {string} query - Texto digitado pelo usuário
 * @returns {Promise<Array>} - Array de sugestões de endereços
 */
export const searchAddressSuggestions = async (query) => {
  if (!query || query.length < 3) return [];

  // Se já sabemos que há um erro de autenticação, retornar erro imediatamente
  if (authErrorOccurred) {
    throw new Error('Erro de autenticação na API do Google Maps');
  }

  try {
    const { autocompleteService } = await initServices();

    // Configuração da busca
    const request = {
      input: query,
      componentRestrictions: { country: 'br' },
      types: ['address']
    };

    // Usando a API do Google Maps diretamente
    return new Promise((resolve, reject) => {
      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        try {
          checkApiStatus(status);

          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            return resolve([]);
          }

          const results = predictions.map(prediction => ({
            id: prediction.place_id,
            description: prediction.description,
            mainText: prediction.structured_formatting.main_text,
            secondaryText: prediction.structured_formatting.secondary_text
          }));

          resolve(results);
        } catch (error) {
          reject(error);
        }
      });
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Obtém detalhes de um local pelo seu ID
 * @param {string} placeId - ID do local
 * @returns {Promise<Object>} - Detalhes do local
 */
export const getPlaceDetails = async (placeId) => {
  if (!placeId) return null;
  if (authErrorOccurred) {
    throw new Error('Erro de autenticação na API do Google Maps');
  }

  // força uso de próxima chave e reinicia serviços
  resetApiLoadingState();
  autocompleteService = null;
  placesService = null;

  try {
    const { placesService } = await initServices();

    // Configuração da busca de detalhes
    const request = {
      placeId,
      fields: ['address_components', 'geometry', 'formatted_address']
    };

    // Usando a API do Google Maps diretamente
    return new Promise((resolve, reject) => {
      placesService.getDetails(request, (place, status) => {
        try {
          checkApiStatus(status);

          if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
            return resolve(null);
          }

          // Extrair componentes do endereço
          const getAddressComponent = (type, useLongName = true) => {
            const component = place.address_components.find(comp => comp.types.includes(type));
            return component ? (useLongName ? component.long_name : component.short_name) : '';
          };

          const details = {
            placeId: place.place_id,
            formattedAddress: place.formatted_address,
            logradouro: getAddressComponent('route'),
            numero: getAddressComponent('street_number'),
            bairro: getAddressComponent('sublocality') ||
              getAddressComponent('sublocality_level_1') ||
              getAddressComponent('sublocality_level_2') ||
              getAddressComponent('neighborhood') ||
              getAddressComponent('political'),
            cidade: getAddressComponent('locality') ||
              getAddressComponent('administrative_area_level_2') ||
              getAddressComponent('sublocality') ||
              getAddressComponent('postal_town'),
            estado: getAddressComponent('administrative_area_level_1', false), // usar short_name para obter a sigla
            cep: getAddressComponent('postal_code'),
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          };

          resolve(details);
        } catch (error) {
          reject(error);
        }
      });
    });
  } catch (error) {
    throw error;
  }
};

// Método para verificar se houve erro de autenticação
export const hasAuthError = () => {
  return authErrorOccurred;
};

// Exporta métodos para uso em outros componentes
export default {
  searchAddressSuggestions,
  getPlaceDetails,
  hasAuthError
};
